// src/engine/gapAnalysis.ts

import { Question, UserAnswer, CriticalGap } from '../types';

export function detectCriticalGaps(
  questions: Question[],
  answers: UserAnswer[]
): CriticalGap[] {
  const answerMap = new Map(answers.map(a => [a.questionId, a.value]));
  const gaps: CriticalGap[] = [];

  // Define critical conditions
  const criticalConditions: Array<{
    questionId: string;
    badValues: string[];
    gap: Omit<CriticalGap, 'severity'> & { severity: 'CRITICAL' | 'HIGH' | 'MODERATE' };
  }> = [
    {
      questionId: 'Q27',
      badValues: ['no', 'unsure'],
      gap: {
        title: 'No multi-factor authentication (MFA)',
        regulation: '2026 HIPAA Security Rule - MFA now mandatory',
        severity: 'CRITICAL',
        action: 'Enable MFA for all user accounts immediately',
        timeframe: 'Week 1 (Days 3-4)',
        estimatedCost: '$500-1,000 (AWS IAM MFA is free)',
      },
    },
    {
      questionId: 'Q29',
      badValues: ['transit', 'no', 'unsure'],
      gap: {
        title: 'Incomplete or missing encryption',
        regulation: '§164.312(a)(2)(iv) - Encryption (Addressable)',
        severity: 'CRITICAL',
        action: 'Enable encryption at rest (S3 KMS, RDS encryption) and in transit (TLS 1.2+)',
        timeframe: 'Week 2 (Days 8-10)',
        estimatedCost: '$200-500/month (KMS keys)',
      },
    },
    {
      questionId: 'Q30',
      badValues: ['partial', 'no', 'unsure'],
      gap: {
        title: 'Insufficient or missing audit logging',
        regulation: '§164.312(b) - Audit Controls (REQUIRED)',
        severity: 'CRITICAL',
        action: 'Enable comprehensive audit logging (CloudTrail, CloudWatch Logs) with 6-year retention',
        timeframe: 'Week 1 (Days 5-7)',
        estimatedCost: '$100-300/month',
      },
    },
    {
      questionId: 'Q11',
      badValues: ['partial', 'no', 'planning'],
      gap: {
        title: 'No comprehensive risk analysis in last 12 months',
        regulation: '§164.308(a)(1)(ii)(A) - Risk Analysis (REQUIRED)',
        severity: 'CRITICAL',
        action: 'Conduct HIPAA risk analysis using NIST framework, document all findings',
        timeframe: 'Week 3 (Days 15-17)',
        estimatedCost: '$3,000-10,000 (consultant optional)',
      },
    },
    {
      questionId: 'Q13',
      badValues: ['unclear', 'no', 'unsure'],
      gap: {
        title: 'No designated HIPAA Security Official',
        regulation: '§164.308(a)(2) - Assigned Security Responsibility (REQUIRED)',
        severity: 'HIGH',
        action: 'Designate a HIPAA Security Official with documented responsibilities',
        timeframe: 'Week 1 (Day 1)',
        estimatedCost: '$0 (internal role)',
      },
    },
    {
      questionId: 'Q42',
      badValues: ['some', 'no', 'unsure'],
      gap: {
        title: 'Missing BAAs with AI/ML vendors',
        regulation: '§164.308(b)(1) - Business Associate Contracts (REQUIRED)',
        severity: 'CRITICAL',
        action: 'Execute BAAs with all AI vendors (AWS Bedrock, OpenAI, etc.) handling PHI',
        timeframe: 'Week 1 (Days 1-2)',
        estimatedCost: '$0 (AWS/Azure/GCP provide free BAAs)',
      },
    },
    {
      questionId: 'Q40',
      badValues: ['no', 'unsure'],
      gap: {
        title: 'No BAA with cloud provider',
        regulation: 'AWS/Azure/GCP BAA requirements',
        severity: 'CRITICAL',
        action: 'Sign BAA with your cloud provider (AWS, Azure, or GCP)',
        timeframe: 'Week 1 (Days 1-2)',
        estimatedCost: '$0 (cloud providers offer free BAAs)',
      },
    },
    {
      questionId: 'Q20',
      badValues: ['most', 'no', 'unsure'],
      gap: {
        title: 'Missing BAAs with some vendors',
        regulation: '§164.308(b)(1) - Business Associate Contracts (REQUIRED)',
        severity: 'HIGH',
        action: 'Inventory all vendors with PHI access and execute missing BAAs',
        timeframe: 'Week 3 (Days 18-19)',
        estimatedCost: '$0',
      },
    },
    {
      questionId: 'Q17',
      badValues: ['partial', 'no', 'unsure'],
      gap: {
        title: 'Incomplete contingency planning',
        regulation: '§164.308(a)(7) - Contingency Plan (REQUIRED)',
        severity: 'HIGH',
        action: 'Create data backup, disaster recovery, and emergency operations plans',
        timeframe: 'Week 2 (Days 11-12)',
        estimatedCost: '$500-2,000',
      },
    },
    {
      questionId: 'Q34',
      badValues: ['basic', 'no', 'unsure'],
      gap: {
        title: 'No detailed breach response plan',
        regulation: '§164.404 - Breach notification requirements',
        severity: 'HIGH',
        action: 'Create breach response plan with 60-day notification templates and procedures',
        timeframe: 'Week 3 (Days 20-21)',
        estimatedCost: '$0-1,000',
      },
    },
    {
      questionId: 'Q32',
      badValues: ['partial', 'no', 'unsure'],
      gap: {
        title: 'No network segmentation',
        regulation: '2026 HIPAA Security Rule - Network segmentation required',
        severity: 'HIGH',
        action: 'Implement network segmentation (VPCs, VLANs) to isolate ePHI systems',
        timeframe: 'Week 2 (Days 11-12)',
        estimatedCost: '$1,000-3,000',
      },
    },
    {
      questionId: 'Q43',
      badValues: ['partial', 'no', 'unsure'],
      gap: {
        title: 'No audit logging for AI interactions',
        regulation: '§164.312(b) - Audit controls for AI systems',
        severity: 'HIGH',
        action: 'Enable logging for all AI prompts/responses containing PHI',
        timeframe: 'Week 1 (Days 5-7)',
        estimatedCost: '$100-200/month',
      },
    },
    {
      questionId: 'Q26',
      badValues: ['mostly', 'shared', 'unsure'],
      gap: {
        title: 'Shared user accounts detected',
        regulation: '§164.312(a)(2)(i) - Unique user identification (REQUIRED)',
        severity: 'MODERATE',
        action: 'Eliminate all shared accounts, assign unique user IDs to all users',
        timeframe: 'Week 2 (Days 13-14)',
        estimatedCost: '$0-500',
      },
    },
    {
      questionId: 'Q15',
      badValues: ['sporadic', 'no', 'onlynew'],
      gap: {
        title: 'No annual HIPAA training',
        regulation: '§164.308(a)(5) - Security awareness and training',
        severity: 'MODERATE',
        action: 'Implement annual HIPAA security training for all workforce members',
        timeframe: 'Week 3-4 (Days 22-30)',
        estimatedCost: '$500-2,000 annually',
      },
    },
    {
      questionId: 'Q44',
      badValues: ['unsure', 'trains'],
      gap: {
        title: 'Unclear if AI models train on your PHI',
        regulation: 'AI data usage restrictions',
        severity: 'HIGH',
        action: 'Verify vendor contracts prohibit training on PHI; enable zero-retention settings',
        timeframe: 'Week 1 (Days 1-2)',
        estimatedCost: '$0',
      },
    },
    {
      questionId: 'Q36',
      badValues: ['late', 'nonotify'],
      gap: {
        title: 'Failed to notify breach within 60 days',
        regulation: '§164.404(b) - 60-day notification requirement (REQUIRED)',
        severity: 'CRITICAL',
        action: 'Immediate legal consultation required; implement breach notification procedures',
        timeframe: 'IMMEDIATE',
        estimatedCost: '$10,000+ (legal, potential fines)',
      },
    },
  ];

  // Check each critical condition
  criticalConditions.forEach((condition) => {
    const answer = answerMap.get(condition.questionId);
    if (answer && condition.badValues.includes(answer as string)) {
      gaps.push(condition.gap);
    }
  });

  // Sort by severity: CRITICAL > HIGH > MODERATE
  return gaps.sort((a, b) => {
    const severityOrder = { CRITICAL: 0, HIGH: 1, MODERATE: 2 };
    return severityOrder[a.severity] - severityOrder[b.severity];
  });
}
